import React, { useEffect, useRef, useState } from 'react'
import { IconButton, TableCell, TableRow } from '@mui/material'
import { Transition } from 'react-transition-group'
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material'
import { CollapsableRow, Row } from '../../../store/types/utils/DataTableUtils'
import { t } from 'i18next'

type RowProps = {
  data: CollapsableRow
  renderRow: (row: CollapsableRow) => React.ReactNode
  renderCollapsedRow: (row: Row, open: boolean) => React.ReactNode
  hasCollapsedRows?: boolean
}

const CollapsibleTableRow = ({ data, renderRow, renderCollapsedRow, hasCollapsedRows }: RowProps) => {
  const [open, setOpen] = useState(false)
  const nodeRef = useRef(null)

  useEffect(() => {
    setOpen(false)
  }, [data])

  const handleRowClick = () => {
    if (hasCollapsedRows) {
      setOpen(!open)
    }
  }

  return (
    <>
      <TableRow
        sx={{
          cursor: hasCollapsedRows ? 'pointer' : 'default',
          minHeight: 0,
          '&:hover': {
            backgroundColor: hasCollapsedRows ? 'background.elevation5' : 'inherit',
          },
          '@supports (-moz-appearance:none)': {
            '.MuiTableCell-root': { height: '36px', minHeight: '36px' },
          },
        }}
        onClick={handleRowClick}
      >
        <TableCell sx={{ py: hasCollapsedRows ? 0 : 1, pr: 0, pl: { xs: 0, sm: 1 } }}>
          {hasCollapsedRows && (
            <IconButton
              size="small"
              aria-label={open ? t('components.vacationTable.row.collapse') : t('components.vacationTable.row.expand')}
            >
              {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
            </IconButton>
          )}
        </TableCell>
        {renderRow(data)}
      </TableRow>
      <Transition in={open} timeout={300} nodeRef={nodeRef}>
        {state => (
          <>
            {data.collapsedData &&
              data.collapsedData.map(row => (
                <TableRow
                  key={row.id}
                  sx={{
                    height: state === 'entered' ? 'auto' : 'fit-content',
                    minHeight: 0,
                    overflow: 'hidden',
                    transition: 'height 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
                  }}
                >
                  {renderCollapsedRow(row, open)}
                </TableRow>
              ))}
          </>
        )}
      </Transition>
    </>
  )
}

export default CollapsibleTableRow
